import type { FormConfigProperty } from '@/types/ui';
import { isNumber } from './types';

export function getEqual<T>(property: keyof T, value: unknown, isNumber?: boolean): string {
  if (isNumber) return `${String(property)} eq ${value}`;
  return `${String(property)} eq '${value}'`;
}

export function getNotEqual<T>(property: keyof T, value: unknown, isNumber?: boolean): string {
  if (isNumber) return `${String(property)} ne ${value}`;
  return `${String(property)} ne '${value}'`;
}

export function getIn<T>(property: keyof T, value: unknown[]): string {
  if (isNumber(value)) return `${String(property)} eq ${value}`;
  return `${String(property)} in '${value.join(';')}'`;
}

export function getContains<T>(property: keyof T, value: unknown): string {
  return `${String(property)} contains '${value}'`;
}

export function lessThan<T>(property: keyof T, value: unknown, isNumber?: boolean): string {
  if (isNumber) return `${String(property)} lt ${value}`;
  return `${String(property)} lt '${value}'`;
}
export function lessThanEqual<T>(property: keyof T, value: unknown, isNumber?: boolean): string {
  if (isNumber) return `${String(property)} lte ${value}`;
  return `${String(property)} lte '${value}'`;
}

export function greaterThan<T>(property: keyof T, value: unknown, isNumber?: boolean): string {
  if (isNumber) return `${String(property)} gt ${value}`;
  return `${String(property)} gt '${value}'`;
}
export function greaterThanEqual<T>(property: keyof T, value: unknown, isNumber?: boolean): string {
  if (isNumber) return `${String(property)} gte ${value}`;
  return `${String(property)} gte '${value}'`;
}

export const enforcedNumberFilterFields = [
  'id',
  'quantity',
  'quantityMeasuredKg',
  'quantityMeasuredL15',
  'quantityPlannedKg',
  'quantityPlannedL15',
  'distance',
  'importStatus'
];

export const enforcedDateFilterFields = [
  'dateDocument',
  'deliveryDate',
  'dischargeDate',
  'entryDate',
  'nominationBillOfLadingDate',
  'postingDate',
  'ticketPostingDate',
  'movementDate'
];

export function getFilterList<EntityType extends object, CreateType extends object>(
  filters: Record<keyof EntityType, unknown>,
  formConfigMap?: Record<keyof CreateType, FormConfigProperty<EntityType, CreateType>>
): string[] {
  const tmp: string[] = [];
  (Object.keys(filters) as (keyof EntityType)[]).forEach((property) => {
    const value = filters[property];
    // @ts-expect-error TS can't handle this properly.
    if ([null, undefined, ''].includes(value)) return;
    if (
      formConfigMap &&
      formConfigMap[property] &&
      (formConfigMap[property].type === 'number' ||
        formConfigMap[property].relationType === 'number')
    ) {
      tmp.push(getEqual<EntityType>(property, value, true));
    } else if (
      formConfigMap &&
      formConfigMap[property] &&
      formConfigMap[property].type === 'date'
    ) {
      const [from, to] = value;
      tmp.push(greaterThanEqual<EntityType>(property, from));
      tmp.push(lessThanEqual<EntityType>(property, to));
    } else if (
      formConfigMap &&
      formConfigMap[property] &&
      formConfigMap[property].type === 'checkbox'
    ) {
      tmp.push(getEqual<EntityType>(property, value === 'on', true));
    } else if (
      formConfigMap &&
      formConfigMap[property] &&
      formConfigMap[property].type === 'checkbox-numeric'
    ) {
      tmp.push(getEqual<EntityType>(property, value, true));
    } else if (
      formConfigMap &&
      formConfigMap[property] &&
      formConfigMap[property].type === 'relation'
    ) {
      tmp.push(getEqual<EntityType>(property, value, false));
    } else if (enforcedNumberFilterFields.includes(property as string)) {
      tmp.push(getEqual<EntityType>(property, value, true));
    } else if (enforcedDateFilterFields.includes(property as string)) {
      const [from, to] = value;
      tmp.push(greaterThanEqual<EntityType>(property, from));
      tmp.push(lessThanEqual<EntityType>(property, to));
    } else {
      tmp.push(getContains(property, value));
    }
  });

  return tmp;
}
